import { config } from "../config";
import type { CartAddon } from "./cart";
import { callGateway, type CallerIdentity } from "./gateway";
import { rupeeGross } from "./priceList";

/**
 * Recharge a subscriber from the console -- radaccess
 * /access/operator/{ns}/subscribers/{AccountID}/recharge (owner 2026-10-10:
 * the Recharge link went to the old PHP page). RBAC resource
 * `subscriberrecharge` (superadmin and Operator).
 *
 * GET lists the account's active devices and the namespace's plans; POST gives
 * the plan to every device and records ONE receipt for the money taken. The
 * plan's validity is read from the plan itself (Expire-After / Expiration),
 * never from its name; the amount is what the operator collected.
 */
export interface RechargeDevice {
  username: string;
  /** Current plan, "" when none. */
  plan: string;
  /** Current expiry as the database writes it, "" when none. */
  expiry: string;
  /** A sign-in password exists; without one the device cannot be renewed. */
  credential: boolean;
}

export interface RechargePlan {
  groupname: string;
  /** Expire-After in seconds; 0 when unset. */
  expireafter: number;
  /** Fixed end as the catalog writes it ("30 Nov 2026 18:29:59", UTC); "" when unset. */
  expiration: string;
  /** "spends": uses one PIN; "unmarked": a PIN provider's plan not marked, refused; "": no PIN. */
  pin: "" | "spends" | "unmarked";
  listprice: number | null;
  gstpercent: number | null;
  /** The linked price row's labels (radaccess v1.178+): a basic plan is one product + speed; absent before. */
  product?: string;
  speed_mbps?: string;
  term_days?: string;
}

export interface RechargeOptions {
  devices: RechargeDevice[];
  plans: RechargePlan[];
}

export type RechargeMethod = "cash" | "upi";

export interface RechargeInput {
  groupname: string;
  amount: number;
  method: RechargeMethod;
  reference: string;
  /** One per attempt: a retried click answers with the receipt already made. */
  requestid: string;
  /** Subscriber cart v1: left out when none; `amount` must then equal the quote total. */
  addons?: CartAddon[];
  /**
   * While a plan is still active (owner 2026-10-24, the subscriber chooses):
   * "after" -- the new plan's full days start when the current one ends;
   * "now" -- it starts now and the unused value of the current plan buys
   * extra days. Left out when nothing is active.
   */
  start?: RechargeStart;
  /** Cash only, where the namespace asks for it (api/cashCode.ts). */
  cash_code?: string;
}

export type RechargeStart = "after" | "now";

/**
 * The latest expiry among the account's devices when it is still ahead of
 * now, "" otherwise. Expiry is IST wall-clock as the database writes it
 * ("2026-11-17 08:15").
 */
/** Owner 2026-10-25: an advance recharge is normally taken up to this many days before expiry. */
export const ADVANCE_RECHARGE_DAYS = 5;

/**
 * Whole days left (rounded up) until an IST "YYYY-MM-DD HH:MM" expiry; 0 when
 * unset, unreadable or past.
 */
export function daysLeft(expiry: string, nowMs: number): number {
  if (!expiry) return 0;
  const ms = Date.parse(`${expiry.trim().replace(" ", "T")}:00+05:30`);
  if (!Number.isFinite(ms) || ms <= nowMs) return 0;
  return Math.ceil((ms - nowMs) / 86_400_000);
}

/**
 * The warning shown when more than ADVANCE_RECHARGE_DAYS are left (owner
 * 2026-10-25: warn, the operator may go ahead when the subscriber asks); ""
 * otherwise.
 */
export function advanceRechargeWarning(expiry: string, nowMs: number): string {
  const n = daysLeft(expiry, nowMs);
  if (n <= ADVANCE_RECHARGE_DAYS) return "";
  return `This subscriber still has ${n} days left (until ${expiry} IST). A recharge is normally taken up to ${ADVANCE_RECHARGE_DAYS} days before expiry.`;
}

/** An IST "YYYY-MM-DD HH:MM" expiry as epoch ms, or null when unset or unreadable. */
export function expiryMs(expiry: string | undefined): number | null {
  if (!expiry) return null;
  const ms = Date.parse(`${expiry.trim().replace(" ", "T")}:00+05:30`);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Time left on a plan in words (owner 2026-10-28, Billing › Recharge): "45 min
 * left", "5 h left", "3 days left", "Expired 2 h ago", "Expired 4 days ago";
 * "" when there is no expiry.
 */
export function timeLeftLabel(expiry: string | undefined, nowMs: number): string {
  const ms = expiryMs(expiry);
  if (ms == null) return "";
  const diff = ms - nowMs;
  const abs = Math.abs(diff);
  const words =
    abs < 3_600_000 ? `${Math.max(1, Math.round(abs / 60_000))} min` : abs < 172_800_000 ? `${Math.floor(abs / 3_600_000)} h` : `${Math.floor(abs / 86_400_000)} days`;
  return diff > 0 ? `${words} left` : `Expired ${words} ago`;
}

/**
 * Billing › Recharge order (owner 2026-10-28, "due soonest first"): plans
 * still running, the one ending soonest first; then expired ones, the most
 * recently expired first; then subscribers with no plan, in the order given.
 */
export function dueOrder<T extends { expiry?: string }>(rows: T[], nowMs: number): T[] {
  const rank = (r: T): [number, number] => {
    const ms = expiryMs(r.expiry);
    if (ms == null) return [2, 0];
    return ms > nowMs ? [0, ms] : [1, -ms];
  };
  return rows
    .map((r, i) => ({ r, i, k: rank(r) }))
    .sort((a, b) => a.k[0] - b.k[0] || a.k[1] - b.k[1] || a.i - b.i)
    .map((x) => x.r);
}

/**
 * The plan to pre-select (owner 2026-10-28, "single click"): the plan of the
 * latest-ending device, when the namespace still offers it and it can be
 * recharged; "" otherwise.
 */
export function previousPlan(devices: RechargeDevice[], plans: RechargePlan[], now: Date = new Date()): string {
  let best = "";
  let bestMs = -Infinity;
  for (const d of devices) {
    if (!d.plan) continue;
    const ms = expiryMs(d.expiry) ?? -Infinity;
    if (best === "" || ms > bestMs) {
      best = d.plan;
      bestMs = ms;
    }
  }
  const p = plans.find((x) => x.groupname === best);
  return p && whyNotRechargeable(p, now) === "" ? best : "";
}

/** The warning when the operator picks a different plan from the subscriber's current one; "" otherwise. */
export function planChangeWarning(previous: string, chosen: string): string {
  if (!previous || !chosen || previous === chosen) return "";
  return `You are changing this subscriber's plan from ${previous} to ${chosen}.`;
}

export function activePlanUntil(devices: RechargeDevice[], nowMs: number): string {
  let best = "";
  let bestMs = nowMs;
  for (const d of devices) {
    if (!d.expiry) continue;
    const ms = Date.parse(`${d.expiry.trim().replace(" ", "T")}:00+05:30`);
    if (Number.isFinite(ms) && ms > bestMs) {
      best = d.expiry;
      bestMs = ms;
    }
  }
  return best;
}

/**
 * One receipt of a sale (owner 2026-10-15/17): "internet" holds the plan and
 * internet add-ons; "other" the non-internet items (router, OTT,
 * installation…), on their own receipt.
 */
export interface SaleReceipt {
  kind: "internet" | "other";
  receiptno: string;
  amount?: number;
}

/** A sale's receipts, internet first; a pre-split radaccess sends only receiptno. */
export function saleReceipts(r: { receiptno?: string; receipts?: SaleReceipt[] }): SaleReceipt[] {
  if (r.receipts && r.receipts.length > 0) {
    return [...r.receipts].sort((a, b) => (a.kind === b.kind ? 0 : a.kind === "internet" ? -1 : 1));
  }
  return r.receiptno ? [{ kind: "internet", receiptno: r.receiptno }] : [];
}

export const receiptKindLabel = (kind: SaleReceipt["kind"]): string => (kind === "other" ? "Other items receipt" : "Internet receipt");

export interface RechargeResult {
  /** The internet receipt (the plan's); every receipt is in receipts. */
  receiptno: string;
  receipts?: SaleReceipt[];
  /** RFC 3339, UTC. */
  expiry?: string;
  devices?: string[];
  amount?: number;
  /** True when this request id had already been recorded. */
  replayed?: boolean;
}

const base = (ns: string, accountId: string) =>
  `/operator/${encodeURIComponent(ns)}/subscribers/${encodeURIComponent(accountId)}/recharge`;

export async function getRechargeOptions(identity: CallerIdentity, ns: string, accountId: string, signal?: AbortSignal): Promise<RechargeOptions> {
  const r = await callGateway<RechargeOptions | null>(identity, config().gatewayBase, base(ns, accountId), signal ? { signal } : {});
  return { devices: r.data?.devices ?? [], plans: r.data?.plans ?? [] };
}

export async function rechargeSubscriber(identity: CallerIdentity, ns: string, accountId: string, input: RechargeInput): Promise<RechargeResult> {
  const r = await callGateway<RechargeResult>(identity, config().gatewayBase, base(ns, accountId), { method: "POST", body: input });
  return r.data;
}

/** "30 days", "6 hours", "until 30 Nov 2026" -- the plan's own validity, in words. */
export function validityLabel(p: RechargePlan): string {
  const parts: string[] = [];
  if (p.expireafter > 0) {
    const s = p.expireafter;
    if (s % 86400 === 0) parts.push(`${s / 86400} ${s === 86400 ? "day" : "days"}`);
    else if (s % 3600 === 0) parts.push(`${s / 3600} ${s === 3600 ? "hour" : "hours"}`);
    else parts.push(`${Math.round(s / 60)} min`);
  }
  if (p.expiration) parts.push(`until ${p.expiration.split(" ").slice(0, 3).join(" ")}`);
  return parts.join(", ") || "no validity set";
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * The catalog's Expiration ("30 Nov 2026 18:29:59", UTC) as epoch ms, or null.
 * Strict, as the server is (Go layout "02 Jan 2006 15:04:05"): Date.parse
 * would also take "2026-11-30" and offer a plan the server refuses.
 */
export function parseExpiration(s: string): number | null {
  const m = /^(\d{2}) ([A-Z][a-z]{2}) (\d{4}) (\d{2}):(\d{2}):(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const month = MONTHS.indexOf(m[2] ?? "");
  if (month < 0) return null;
  return Date.UTC(Number(m[3]), month, Number(m[1]), Number(m[4]), Number(m[5]), Number(m[6]));
}

/** Why a plan cannot be chosen, or "" when it can. Mirrors the server's refusals. */
export function whyNotRechargeable(p: RechargePlan, now: Date = new Date()): string {
  if (p.pin === "unmarked") return "Not marked as a PIN plan — mark it under PIN plans first";
  if (p.expireafter <= 0 && !p.expiration) return "This plan has no validity set";
  if (p.expiration) {
    const end = parseExpiration(p.expiration);
    if (end == null) return "This plan's end date cannot be read";
    if (end <= now.getTime()) return "This plan's end date has passed";
  }
  return "";
}

/** Why the devices block a recharge, or "". */
export function whyDevicesBlock(devices: RechargeDevice[]): string {
  if (devices.length === 0) return "This subscriber has no active device";
  const bad = devices.find((d) => !d.credential);
  return bad ? `Device ${bad.username} has no sign-in password; it cannot be renewed` : "";
}

/** List price including GST, rounded to the rupee; null when no price is listed. */
export function listPriceWithGST(p: RechargePlan): number | null {
  if (p.listprice == null) return null;
  return rupeeGross(p.listprice, p.gstpercent ?? 0);
}

/** The form's own check, matching the server's; "" when it may be sent. */
export function formError(f: { groupname: string; amount: string; method: RechargeMethod; reference: string }): string {
  if (!f.groupname) return "Choose a plan";
  if (!/^\d{1,6}$/.test(f.amount.trim()) || Number(f.amount) > 100000) return "Amount must be whole rupees, 0 to 100000";
  const ref = f.reference.trim();
  if (f.method === "upi" && !/^[A-Za-z0-9/_-]{6,40}$/.test(ref)) return "Enter the UPI reference (UTR), 6 to 40 letters or digits";
  if (f.method === "cash" && ref) return "A cash payment has no reference";
  return "";
}

/**
 * Console Pay online (owner 2026-10-28, radaccess .../recharge/online): the
 * subscriber pays a gateway payment link -- the QR on screen, or the link the
 * gateway texts (owner POL-2) -- valid 24 hours (POL-1). The recharge decided
 * here is stored and applied once the gateway says paid.
 */
export interface PayOnlineInput {
  groupname: string;
  amount: number;
  requestid: string;
  addons?: CartAddon[];
  start?: RechargeStart;
  notify_sms: boolean;
}

export type PayOnlineStatus = "Pending" | "Applied" | "Expired" | "Refused";

export interface PayOnlineView {
  orderid: string;
  status: PayOnlineStatus;
  amount: number;
  checkouturl: string;
  /** RFC 3339, UTC. */
  expiresat: string;
  sms: boolean;
  receiptnos?: string;
  message?: string;
}

const onlineBase = (ns: string, accountId: string) => `${base(ns, accountId)}/online`;

export async function startPayOnline(identity: CallerIdentity, ns: string, accountId: string, input: PayOnlineInput): Promise<PayOnlineView> {
  const r = await callGateway<PayOnlineView>(identity, config().gatewayBase, onlineBase(ns, accountId), { method: "POST", body: input });
  return r.data;
}

export async function getPayOnline(identity: CallerIdentity, ns: string, accountId: string, orderId: string, signal?: AbortSignal): Promise<PayOnlineView> {
  const r = await callGateway<PayOnlineView>(identity, config().gatewayBase, `${onlineBase(ns, accountId)}/${encodeURIComponent(orderId)}`, signal ? { signal } : {});
  return r.data;
}

/** What the waiting dialog says for a link's state. */
export function payOnlineStatusText(v: PayOnlineView): string {
  switch (v.status) {
    case "Applied":
      return `Paid and recharged${v.receiptnos ? `: receipt ${v.receiptnos}` : ""}.`;
    case "Expired":
      return "The link expired unpaid. Start a new one, or take cash or UPI.";
    case "Refused":
      return v.message || "Paid, but the recharge could not be applied. Refund or recharge by hand.";
    default:
      return v.message || "Waiting for the subscriber to pay…";
  }
}
